import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, switchMap, map, catchError, tap } from 'rxjs/operators';
import { of, from } from 'rxjs';
import { CustomersActions } from './customers.actions';
import { CustomerService } from '../../services/customer.service';
import { decryptCustomerRecord } from '../../models/customer-decrypt.util';
import { EncryptionService } from '../../zk/encryption.service';
import { streamDecryptedBatches$ } from '../../zk/batch-decrypt.util';
import { AuditService } from '../../zk/audit.service';

@Injectable()
export class CustomersEffects {
  private actions$ = inject(Actions);
  private customerService = inject(CustomerService);
  private encryption = inject(EncryptionService);
  private audit = inject(AuditService);

  // ZK Phase 9.5 — decrypts in batches of 50 (see zk/batch-decrypt.util.ts) and dispatches
  // one action per batch so the list renders progressively instead of blocking on the
  // whole candidate set decrypting at once.
  loadCustomers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomersActions.loadCustomers),
      switchMap(({ areaId }) =>
        this.customerService.getAll(areaId).pipe(
          mergeMap(records => streamDecryptedBatches$(
            records,
            r => decryptCustomerRecord(this.encryption, r),
            customers => CustomersActions.loadCustomersBatchSuccess({ customers }),
            customers => CustomersActions.loadCustomersSuccess({ customers }),
          )),
          catchError(err => of(CustomersActions.loadCustomersFailure({ error: err.message })))
        )
      )
    )
  );

  loadCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomersActions.loadCustomer),
      mergeMap(({ id }) =>
        this.customerService.getById(id).pipe(
          mergeMap(record => from(decryptCustomerRecord(this.encryption, record))),
          map(customer => CustomersActions.loadCustomerSuccess({ customer })),
          catchError(err => of(CustomersActions.loadCustomerFailure({ error: err.message })))
        )
      )
    )
  );

  createCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomersActions.createCustomer),
      mergeMap(({ dto }) =>
        this.customerService.createEncrypted(dto).pipe(
          mergeMap(record => from(decryptCustomerRecord(this.encryption, record))),
          map(customer => CustomersActions.createCustomerSuccess({ customer })),
          catchError(err => of(CustomersActions.createCustomerFailure({ error: err.message })))
        )
      )
    )
  );

  updateCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomersActions.updateCustomer),
      mergeMap(({ id, dto }) =>
        this.customerService.updateEncrypted(id, dto).pipe(
          mergeMap(record => from(decryptCustomerRecord(this.encryption, record))),
          map(customer => CustomersActions.updateCustomerSuccess({ customer })),
          catchError(err => of(CustomersActions.updateCustomerFailure({ error: err.message })))
        )
      )
    )
  );

  deleteCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomersActions.deleteCustomer),
      mergeMap(({ id }) =>
        this.customerService.delete(id).pipe(
          map(() => CustomersActions.deleteCustomerSuccess({ id })),
          catchError(err => of(CustomersActions.deleteCustomerFailure({ error: err.message })))
        )
      )
    )
  );

  // Phase 8.6 — audit the one client-side event the server has no other way to learn
  // about: a detail-view decrypt. Only the single-record load is audited, not the list
  // load, to keep the audit trail meaningful rather than one row per row-on-screen.
  auditDecrypt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomersActions.loadCustomerSuccess),
      tap(({ customer }) => this.audit.log('DECRYPT', 'Customer', customer.id))
    ),
    { dispatch: false }
  );
}
